import { Platform } from 'react-native';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { LIFECYCLE_LOGO_JPEG } from '../assets/logoPdfData';
import { Product } from '../hooks/useProductsList';

const BRAND_PRIMARY = { r: 0, g: 80, b: 110 };
const BRAND_TEAL = { r: 13, g: 164, b: 159 };
const TEXT_ON_BRAND = { r: 255, g: 255, b: 255 };
const TEXT = { r: 31, g: 41, b: 55 };
const POSITIVE = { r: 16, g: 185, b: 129 };
const NEGATIVE = { r: 239, g: 68, b: 68 };
const HEADER_HEIGHT = 16;
const HEADER_ACCENT_HEIGHT = 1.8;
const MONTH_NAMES = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const;

export type ProductsListPdfColumnKey =
  | 'eventDate'
  | 'eventType'
  | 'isin'
  | 'valor'
  | 'name'
  | 'worstUnderlyingName'
  | 'distanceFromAutocall'
  | 'ccy'
  | 'issuer'
  | 'bid'
  | 'ask'
  | 'currentNotional'
  | 'initialFixing'
  | 'finalFixing'
  | 'redemptionDate'
  | 'worstBarrierUnderlyingName'
  | 'worstBarrierUnderlyingWofPct'
  | 'worstBarrierPct'
  | 'worstBarrierUnderlyingDistancePct'
  | 'worstBarrierUnderlyingCurrent'
  | 'worstBarrierUnderlyingBarrier'
  | 'productTypeDisplay'
  | 'worstStrikeUnderlyingName'
  | 'worstStrikeUnderlyingWofPct'
  | 'strikePct'
  | 'worstStrikeUnderlyingDistancePct'
  | 'worstStrikeUnderlyingCurrent'
  | 'worstStrikeUnderlyingStrike';

export interface ProductsListPdfColumn {
  key: ProductsListPdfColumnKey;
  label: string;
}

interface ProductDetailLike {
  name?: string;
  isin?: string;
  issuer?: string;
  currency?: string;
  bid?: number;
  ask?: number;
  issueDate?: string;
  initialFixing?: string;
  redemptionDate?: string;
  coupon?: number | null;
  couponFrequencyName?: string | null;
  couponBarrier?: number | null;
  couponTypeName?: string | null;
  barrierLevel?: number | null;
  barrierStyleName?: string | null;
  upsideParticipation?: number | null;
  capLevel?: number | null;
  bonusLevel?: number | null;
  capitalProtectionPct?: number | null;
  recovery?: string | null;
  quotationType?: string | null;
  basketOptionTypeName?: string | null;
  basketTypeUp?: string | number | null;
  upside?: string | number | null;
  autocallTrigger?: number | null;
  coupons?: Array<{
    paymentDate?: string;
    amount?: string | number | null;
    isPaid?: boolean;
  }>;
  underlyings?: Array<{
    name?: string;
    strikeLevel?: number | null;
    initialFixingLevel?: number | null;
    marketPrice?: number | null;
    performance?: number | null;
    autocallTrigger?: number | null;
    barrierLevel?: number | null;
    barrierDistance?: number | null;
    couponTrigger?: number | null;
  }>;
}

export interface ExportProductsListPdfInput {
  products: Product[];
  columns?: ProductsListPdfColumn[];
  eventType?: string;
  eventBucket?: 'nextWeek' | 'nextMaturity';
  title?: string;
  exporterCompanyName?: string | null;
  fetchProductDetail?: (isin: string) => Promise<ProductDetailLike | null>;
}

interface ReportTerm {
  label: string;
  value: string;
}

interface ReportUnderlying {
  name: string;
  strike: number | null;
  lastPrice: number | null;
  performance: number | null;
  autocall: number | null;
  capitalBarrier: number | null;
  barrierDistance: number | null;
  couponTrigger: number | null;
}

interface ReportCard {
  name: string;
  isin: string;
  issuer: string;
  currency: string;
  invested: number;
  maturity: string;
  issueDate: string;
  nextObservation: string;
  ask: string;
  bid: string;
  presentValue: number | null;
  terms: ReportTerm[];
  underlyings: ReportUnderlying[];
  showCouponBarrierColumn: boolean;
}

const formatDate = (value?: string | null): string => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const formatAmount = (value?: number | null): string => {
  if (value == null || !Number.isFinite(value)) return 'N/A';
  return value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const formatPrice = (value?: number | null): string => {
  if (value == null || !Number.isFinite(value)) return 'N/A';
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const formatMarket = (value?: number | null): string => {
  if (value == null || !Number.isFinite(value) || value === 0) return '/';
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatPct = (value?: number | null, digits = 1): string => {
  if (value == null || !Number.isFinite(value)) return 'N/A';
  return `${value.toFixed(digits)}%`;
};

const formatLevel = (value?: number | null): string => {
  if (value == null || !Number.isFinite(value) || value === 0) return 'N/A';
  return value.toFixed(1);
};

const formatAutocall = (value?: number | null): string => {
  if (value == null || !Number.isFinite(value) || value === 0) return '/';
  if (value > 0 && value <= 200) return `${value.toFixed(1)}%`;
  return formatMarket(value);
};

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const resolveTitle = (input: ExportProductsListPdfInput): string =>
  (input.title || 'Lifecycle Report').trim() || 'Lifecycle Report';

const getFilename = (): string => {
  const now = new Date();
  return `lifecycle_report_${MONTH_NAMES[now.getMonth()]}_${now.getFullYear()}`;
};

const productNotional = (product: Product): number => {
  const value = Number(
    product.allocatedNotional ?? product.currentNotional ?? product.tradedNotional ?? 0
  );
  return Number.isFinite(value) ? value : 0;
};

const asText = (value?: string | number | null): string | null => {
  if (value == null || value === '') return null;
  const text = String(value).trim();
  return text || null;
};

const parseAmount = (value?: string | number | null): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return 0;
  const parsed = Number(value.replace('%', '').replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
};

const nextObservationFromDetail = (
  product: Product,
  detail: ProductDetailLike | null
): string => {
  if (product.nextEventDate) return formatDate(product.nextEventDate);
  const upcoming = (detail?.coupons || []).find(coupon => !coupon.isPaid && coupon.paymentDate);
  return formatDate(upcoming?.paymentDate);
};

const totalPaidPct = (detail: ProductDetailLike | null): string => {
  const paid = (detail?.coupons || []).filter(coupon => coupon.isPaid);
  if (paid.length === 0) return 'N/A';
  const total = paid.reduce((sum, coupon) => sum + parseAmount(coupon.amount), 0);
  return Number.isFinite(total) ? `${total.toFixed(3)}%` : 'N/A';
};

const basketTypeOf = (detail: ProductDetailLike | null, underlyingCount: number): string => {
  const named =
    asText(detail?.basketOptionTypeName) ||
    asText(detail?.basketTypeUp) ||
    (typeof detail?.upside === 'string' ? asText(detail.upside) : null);
  if (named) return named;
  if (underlyingCount > 1) return 'Worst of';
  if (underlyingCount === 1) return 'Mono';
  return 'N/A';
};

const capitalBarrierOf = (detail: ProductDetailLike | null, product: Product): string => {
  const level = detail?.barrierLevel;
  const style = asText(detail?.barrierStyleName) || asText(product.barrierStyleName);
  if ((level == null || !Number.isFinite(level) || level === 0) && !style) return 'None';
  const parts = [
    level != null && Number.isFinite(level) && level !== 0 ? `${level.toFixed(1)}%` : null,
    style,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'None';
};

const buildTerms = (detail: ProductDetailLike | null, product: Product, underlyingCount: number): ReportTerm[] => {
  const coupon = detail?.coupon;
  const isCouponProduct = coupon != null && Number.isFinite(coupon) && coupon !== 0;
  const recovery = asText(detail?.recovery);
  const terms: ReportTerm[] = [];

  if (isCouponProduct) {
    const frequency = asText(detail?.couponFrequencyName);
    terms.push({
      label: 'Coupon',
      value: `${coupon.toFixed(3)}%${frequency ? ` ${frequency}` : ''}`,
    });
    terms.push({ label: 'Coupon Barrier', value: formatPct(detail?.couponBarrier) });
    terms.push({ label: 'Total Paid', value: totalPaidPct(detail) });
  } else {
    terms.push({ label: 'Participation', value: formatPct(detail?.upsideParticipation) });
    terms.push({ label: 'Cap', value: formatLevel(detail?.capLevel) });
    if (detail?.bonusLevel != null && Number.isFinite(detail.bonusLevel) && detail.bonusLevel !== 0) {
      terms.push({ label: 'Bonus Level', value: formatLevel(detail.bonusLevel) });
    }
  }

  terms.push({ label: 'Capital Barrier', value: capitalBarrierOf(detail, product) });
  terms.push({ label: 'Basket Type', value: basketTypeOf(detail, underlyingCount) });
  if (recovery) {
    terms.push({ label: 'Recovery', value: recovery });
  }
  return terms;
};

const fitText = (pdf: import('jspdf').jsPDF, text: string, maxWidth: number): string => {
  const raw = (text || '').replace(/\s+/g, ' ').trim() || 'N/A';
  if (pdf.getTextWidth(raw) <= maxWidth) return raw;
  const ellipsis = '...';
  let low = 0;
  let high = raw.length;
  let best = '';
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const candidate = raw.slice(0, mid);
    if (pdf.getTextWidth(`${candidate}${ellipsis}`) <= maxWidth) {
      best = candidate;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return best ? `${best}${ellipsis}` : ellipsis;
};

const buildCard = (product: Product, detail: ProductDetailLike | null): ReportCard => {
  const invested = productNotional(product);
  const bid = Number(detail?.bid ?? product.bid);
  const ask = Number(detail?.ask ?? product.ask);
  const quotation = (detail?.quotationType || '').toLowerCase();
  const presentValue =
    Number.isFinite(bid) && bid > 0
      ? quotation.includes('unit')
        ? invested * bid
        : invested * (bid / 100)
      : null;
  const underlyings = (detail?.underlyings || []).map(underlying => ({
    name: underlying.name || '-',
    strike: underlying.strikeLevel ?? underlying.initialFixingLevel ?? null,
    lastPrice: underlying.marketPrice ?? null,
    performance: underlying.performance ?? null,
    autocall: underlying.autocallTrigger ?? null,
    capitalBarrier: underlying.barrierLevel ?? null,
    barrierDistance: underlying.barrierDistance ?? null,
    couponTrigger: underlying.couponTrigger ?? null,
  }));
  const showCouponBarrierColumn = underlyings.some(
    underlying => underlying.couponTrigger != null && underlying.couponTrigger !== 0
  );

  return {
    name: detail?.name || product.name || '-',
    isin: detail?.isin || product.isin || '-',
    issuer: detail?.issuer || product.issuer || '-',
    currency: detail?.currency || product.ccy || '-',
    invested,
    maturity: formatDate(detail?.redemptionDate || product.redemptionDate),
    issueDate: formatDate(detail?.issueDate || detail?.initialFixing || product.initialFixing),
    nextObservation: nextObservationFromDetail(product, detail),
    ask: formatPrice(ask),
    bid: formatPrice(bid),
    presentValue,
    terms: buildTerms(detail, product, underlyings.length),
    underlyings,
    showCouponBarrierColumn,
  };
};

const loadCards = async (input: ExportProductsListPdfInput): Promise<ReportCard[]> => {
  const cards: ReportCard[] = [];
  const concurrency = 5;
  for (let index = 0; index < input.products.length; index += concurrency) {
    const batch = input.products.slice(index, index + concurrency);
    const details = await Promise.all(
      batch.map(async product => {
        if (!input.fetchProductDetail || !product.isin) return null;
        try {
          return await input.fetchProductDetail(product.isin);
        } catch {
          return null;
        }
      })
    );
    batch.forEach((product, batchIndex) => {
      cards.push(buildCard(product, details[batchIndex]));
    });
  }
  return cards;
};

const tableRowHeight = (pdf: import('jspdf').jsPDF, name: string, nameWidth: number): number => {
  const lines = pdf.splitTextToSize(name.replace(/\s+/g, ' ').trim() || '-', nameWidth);
  return Math.max(5.1, Math.min(lines.length, 2) * 3.6 + 1.2);
};

const cardHeight = (pdf: import('jspdf').jsPDF, card: ReportCard, tableNameWidth: number): number => {
  const titleLines = pdf.splitTextToSize(card.name, pdf.internal.pageSize.getWidth() - 22);
  const titleH = 3.6 + Math.min(titleLines.length, 2) * 4.1;
  const leftRows = 6;
  const leftH = leftRows * 4.15;
  const termsH = card.terms.length * 4.15;
  const rows = card.underlyings.length > 0 ? card.underlyings : [{ name: '-' } as ReportUnderlying];
  const tableH =
    8 +
    rows.reduce((sum, row) => sum + tableRowHeight(pdf, row.name, tableNameWidth), 0);
  return titleH + 3 + Math.max(leftH, termsH, tableH) + 4;
};

const drawHeaderBar = (
  pdf: import('jspdf').jsPDF,
  title: string
): void => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  pdf.setFillColor(BRAND_PRIMARY.r, BRAND_PRIMARY.g, BRAND_PRIMARY.b);
  pdf.rect(0, 0, pageWidth, HEADER_HEIGHT, 'F');
  pdf.setFillColor(BRAND_TEAL.r, BRAND_TEAL.g, BRAND_TEAL.b);
  pdf.rect(0, HEADER_HEIGHT, pageWidth, HEADER_ACCENT_HEIGHT, 'F');

  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(5, 2, 12, 12, 1.6, 1.6, 'F');
  pdf.addImage(LIFECYCLE_LOGO_JPEG, 'JPEG', 5.9, 2.8, 10.2, 10.4);

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(TEXT_ON_BRAND.r, TEXT_ON_BRAND.g, TEXT_ON_BRAND.b);
  pdf.text('Lifecycle', 19.5, 9.6);

  pdf.setFontSize(14);
  pdf.text(fitText(pdf, title, pageWidth - 110), pageWidth / 2, 10, { align: 'center' });
};

const drawPair = (
  pdf: import('jspdf').jsPDF,
  label: string,
  value: string,
  x: number,
  y: number,
  labelWidth: number,
  valueWidth: number,
  emphasize = false
): void => {
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7);
  pdf.setTextColor(TEXT.r, TEXT.g, TEXT.b);
  pdf.text(`${label}:`, x, y);
  pdf.setFont('helvetica', emphasize ? 'bold' : 'normal');
  pdf.text(fitText(pdf, value || 'N/A', valueWidth), x + labelWidth, y);
};

const TABLE_PAD = 0.8;

const numericHeaders = (showCouponBarrier: boolean): string[] =>
  showCouponBarrier
    ? ['Strike', 'Last', 'Perf', 'Autocall', 'Barrier', 'Dist %', 'Cpn bar.']
    : ['Strike', 'Last', 'Perf', 'Autocall', 'Barrier', 'Dist %'];

const tableColumns = (
  pdf: import('jspdf').jsPDF,
  names: string[],
  showCouponBarrier: boolean,
  tableWidth: number
): Array<{ key: string; width: number; align: 'left' | 'right' }> => {
  const headers = numericHeaders(showCouponBarrier);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(6.4);
  const longestName = names.reduce(
    (max, name) => Math.max(max, pdf.getTextWidth((name || '-').replace(/\s+/g, ' ').trim())),
    pdf.getTextWidth('Underlying')
  );
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(6.2);
  const minNumeric = Math.max(
    ...headers.map(header => pdf.getTextWidth(header) + TABLE_PAD * 2),
    14
  );
  const nameWidth = Math.min(
    Math.max(longestName + 2.4, 22),
    Math.max(22, tableWidth - headers.length * minNumeric)
  );
  const numericWidth = (tableWidth - nameWidth) / headers.length;
  return [
    { key: 'Underlying', width: nameWidth, align: 'left' },
    ...headers.map(key => ({ key, width: numericWidth, align: 'right' as const })),
  ];
};

const drawCard = (pdf: import('jspdf').jsPDF, card: ReportCard, y: number): number => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const x = 5;
  const width = pageWidth - 10;
  const tableX = 148;
  const tableWidth = x + width - tableX - 1;
  const underlyingNames = (card.underlyings.length ? card.underlyings : [{ name: '-' }]).map(
    underlying => underlying.name
  );
  const cols = tableColumns(pdf, underlyingNames, card.showCouponBarrierColumn, tableWidth);
  const nameWidth = cols[0].width - 1.2;
  const height = cardHeight(pdf, card, nameWidth);

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8.4);
  const titleLines = pdf.splitTextToSize(card.name, width - 10).slice(0, 2) as string[];
  const titleH = 3.2 + titleLines.length * 4.1;
  pdf.setFillColor(BRAND_PRIMARY.r, BRAND_PRIMARY.g, BRAND_PRIMARY.b);
  pdf.rect(x, y, width, titleH, 'F');
  pdf.setFillColor(BRAND_TEAL.r, BRAND_TEAL.g, BRAND_TEAL.b);
  pdf.rect(x, y, 1.8, titleH, 'F');
  pdf.setTextColor(TEXT_ON_BRAND.r, TEXT_ON_BRAND.g, TEXT_ON_BRAND.b);
  titleLines.forEach((line, index) => {
    pdf.text(line, x + 5, y + 4.2 + index * 4.1);
  });

  const bodyY = y + titleH + 5.6;
  const leftX = 7;
  const midX = 102;
  const rowH = 4.15;

  const identity = [
    ['ISIN', card.isin],
    ['Issuer', card.issuer],
    ['Currency', card.currency],
    ['Invested', formatAmount(card.invested)],
  ] as const;
  const pricing = [
    ['Maturity', card.maturity],
    ['Issue Date', card.issueDate],
    ['Next Obs', card.nextObservation],
    ['Ask', card.ask],
    ['Bid', card.bid],
    ['Present Value', card.presentValue == null ? 'N/A' : formatAmount(card.presentValue)],
  ] as const;

  identity.forEach(([label, value], index) => {
    drawPair(pdf, label, value, leftX, bodyY + index * rowH, 18, 24);
  });
  pricing.forEach(([label, value], index) => {
    drawPair(
      pdf,
      label,
      value,
      51,
      bodyY + index * rowH,
      24,
      22,
      label === 'Present Value'
    );
  });

  card.terms.forEach((term, index) => {
    drawPair(pdf, term.label, term.value, midX, bodyY + index * rowH, 24, 20);
  });

  let cx = tableX;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(6.2);
  pdf.setTextColor(TEXT.r, TEXT.g, TEXT.b);
  cols.forEach(col => {
    if (col.align === 'right') {
      pdf.text(col.key, cx + col.width - TABLE_PAD, bodyY, { align: 'right' });
    } else {
      pdf.text(col.key, cx + 0.3, bodyY);
    }
    cx += col.width;
  });
  const tableHeaderBottom = bodyY + 2.4;
  pdf.setDrawColor(180, 180, 180);
  pdf.setLineWidth(0.2);
  pdf.line(tableX, tableHeaderBottom, tableX + tableWidth, tableHeaderBottom);

  const rows = card.underlyings.length > 0
    ? card.underlyings
    : [{
        name: '-',
        strike: null,
        lastPrice: null,
        performance: null,
        autocall: null,
        capitalBarrier: null,
        barrierDistance: null,
        couponTrigger: null,
      }];

  let rowY = tableHeaderBottom + 3.6;
  rows.forEach(row => {
    const nameLines = pdf.splitTextToSize(row.name.replace(/\s+/g, ' ').trim() || '-', nameWidth).slice(0, 2) as string[];
    const values = [
      nameLines[0],
      formatMarket(row.strike),
      formatMarket(row.lastPrice),
      row.performance == null ? '/' : `${row.performance.toFixed(2)}%`,
      formatAutocall(row.autocall),
      formatMarket(row.capitalBarrier),
      row.barrierDistance == null || row.barrierDistance === 0
        ? '/'
        : row.barrierDistance.toFixed(2),
    ];
    if (card.showCouponBarrierColumn) {
      values.push(formatMarket(row.couponTrigger));
    }
    cx = tableX;
    const h = tableRowHeight(pdf, row.name, nameWidth);
    cols.forEach((col, index) => {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(6.4);
      if (index === 3 && row.performance != null) {
        const color = row.performance >= 0 ? POSITIVE : NEGATIVE;
        pdf.setTextColor(color.r, color.g, color.b);
      } else {
        pdf.setTextColor(TEXT.r, TEXT.g, TEXT.b);
      }
      if (index === 0) {
        nameLines.forEach((line, lineIndex) => {
          pdf.text(line, cx + 0.3, rowY + lineIndex * 3.4);
        });
      } else if (values[index] === '/') {
        pdf.text('/', cx + col.width / 2, rowY, { align: 'center' });
      } else {
        pdf.text(values[index], cx + col.width - TABLE_PAD, rowY, { align: 'right' });
      }
      cx += col.width;
    });
    rowY += h;
  });

  const bottom = y + height;
  pdf.setDrawColor(180, 180, 180);
  pdf.setLineWidth(0.25);
  pdf.line(x, bottom - 1.1, x + width, bottom - 1.1);
  return bottom;
};

const exportPortfolioReportOnWeb = async (
  input: ExportProductsListPdfInput,
  cards: ReportCard[],
  filename: string
): Promise<void> => {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const title = resolveTitle(input);
  const pageHeight = pdf.internal.pageSize.getHeight();
  const pageBottom = pageHeight - 7;

  const startPage = (): number => {
    drawHeaderBar(pdf, title);
    return HEADER_HEIGHT + HEADER_ACCENT_HEIGHT + 2.5;
  };

  let y = startPage();
  cards.forEach(card => {
    const tableWidth = pdf.internal.pageSize.getWidth() - 164;
    const names = (card.underlyings.length ? card.underlyings : [{ name: '-' }]).map(
      underlying => underlying.name
    );
    const cols = tableColumns(pdf, names, card.showCouponBarrierColumn, tableWidth);
    const height = cardHeight(pdf, card, cols[0].width - 1.2);
    if (y + height > pageBottom) {
      pdf.addPage('a4', 'landscape');
      y = startPage();
    }
    y = drawCard(pdf, card, y);
  });

  const totalPages = pdf.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    pdf.setPage(page);
    const width = pdf.internal.pageSize.getWidth();
    const height = pdf.internal.pageSize.getHeight();
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7);
    pdf.setTextColor(BRAND_PRIMARY.r, BRAND_PRIMARY.g, BRAND_PRIMARY.b);
    pdf.text('Lifecycle', 8, height - 3.5);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(BRAND_TEAL.r, BRAND_TEAL.g, BRAND_TEAL.b);
    pdf.text(`Page ${page} of ${totalPages}`, width - 8, height - 3.5, { align: 'right' });
  }

  pdf.save(`${filename}.pdf`);
};

const buildHtml = (input: ExportProductsListPdfInput, cards: ReportCard[]): string => {
  const title = resolveTitle(input);
  const cardHtml = cards
    .map(card => {
      const rows = (card.underlyings.length ? card.underlyings : [{
        name: '-',
        strike: null,
        lastPrice: null,
        performance: null,
        autocall: null,
        capitalBarrier: null,
        barrierDistance: null,
        couponTrigger: null,
      }])
        .map(row => {
          const perfClass = row.performance == null ? '' : row.performance >= 0 ? 'pos' : 'neg';
          return `<tr>
            <td>${escapeHtml(row.name)}</td>
            <td>${formatMarket(row.strike)}</td>
            <td>${formatMarket(row.lastPrice)}</td>
            <td class="${perfClass}">${row.performance == null ? '/' : `${row.performance.toFixed(2)}%`}</td>
            <td>${formatAutocall(row.autocall)}</td>
            <td>${formatMarket(row.capitalBarrier)}</td>
            <td>${row.barrierDistance == null || row.barrierDistance === 0 ? '/' : row.barrierDistance.toFixed(3)}</td>
            ${card.showCouponBarrierColumn ? `<td>${formatMarket(row.couponTrigger)}</td>` : ''}
          </tr>`;
        })
        .join('');
      const terms = card.terms
        .map(term => `<div><b>${escapeHtml(term.label)}:</b> ${escapeHtml(term.value)}</div>`)
        .join('');
      return `
        <section class="card">
          <h2>${escapeHtml(card.name)}</h2>
          <div class="body">
            <div class="identity">
              <div><b>ISIN:</b> ${escapeHtml(card.isin)}</div>
              <div><b>Maturity:</b> ${escapeHtml(card.maturity)}</div>
              <div><b>Issuer:</b> ${escapeHtml(card.issuer)}</div>
              <div><b>Issue Date:</b> ${escapeHtml(card.issueDate)}</div>
              <div><b>Currency:</b> ${escapeHtml(card.currency)}</div>
              <div><b>Next Obs:</b> ${escapeHtml(card.nextObservation)}</div>
              <div><b>Invested:</b> ${escapeHtml(formatAmount(card.invested))}</div>
              <div><b>Ask:</b> ${escapeHtml(card.ask)}</div>
              <div><b>Bid:</b> ${escapeHtml(card.bid)}</div>
              <div><b>Present Value:</b> ${card.presentValue == null ? 'N/A' : escapeHtml(formatAmount(card.presentValue))}</div>
            </div>
            <div class="terms">${terms}</div>
            <table>
              <thead>
                <tr>
                  <th>Underlying</th><th>Strike</th><th>Last</th><th>Perf</th>
                  <th>Autocall</th><th>Barrier</th><th>Dist %</th>
                  ${card.showCouponBarrierColumn ? '<th>Cpn bar.</th>' : ''}
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>
          </div>
        </section>`;
    })
    .join('');

  return `
    <html>
      <head>
        <meta charset="utf-8" />
        <style>
          @page { size: A4 landscape; margin: 8mm; }
          body { font-family: Helvetica, Arial, sans-serif; margin: 0; color: #1F2937; }
          .header {
            background: #00506E;
            color: #FFFFFF;
            padding: 10px 12px 8px;
            display: flex;
            align-items: center;
            gap: 10px;
            border-bottom: 5px solid #0DA49F;
          }
          .header img {
            height: 28px;
            width: 28px;
            background: #FFFFFF;
            border-radius: 6px;
            padding: 3px;
          }
          .header .brand { font-size: 16px; font-weight: 700; }
          .header .title { flex: 1; text-align: center; font-size: 18px; font-weight: 700; }
          .card { border-bottom: 1px solid #B4B4B4; page-break-inside: avoid; margin-bottom: 6px; }
          h2 { background: #00506E; color: #FFFFFF; font-size: 11px; margin: 0; padding: 5px 8px; text-align: left; border-left: 5px solid #0DA49F; }
          .body { display: flex; gap: 10px; padding: 8px 6px; }
          .identity, .terms { font-size: 10px; }
          .identity { width: 34%; display: grid; grid-template-columns: 1fr 1fr; gap: 2px 8px; }
          .terms { width: 20%; display: flex; flex-direction: column; gap: 2px; }
          table { flex: 1; border-collapse: collapse; font-size: 9px; }
          th, td { padding: 2px 4px; }
          th { font-weight: 700; }
          th:not(:first-child), td:not(:first-child) { text-align: right; }
          .pos { color: #10B981; }
          .neg { color: #EF4444; }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${LIFECYCLE_LOGO_JPEG}" />
          <span class="brand">Lifecycle</span>
          <span class="title">${escapeHtml(title)}</span>
        </div>
        ${cardHtml}
      </body>
    </html>
  `;
};

export const exportProductsListPdf = async (input: ExportProductsListPdfInput): Promise<void> => {
  if (!input.products.length) {
    throw new Error('No products to export.');
  }

  const filename = getFilename();
  const cards = await loadCards(input);

  if (Platform.OS === 'web') {
    await exportPortfolioReportOnWeb(input, cards, filename);
    return;
  }

  const html = buildHtml(input, cards);
  const printResult = await Print.printToFileAsync({ html, base64: false });

  if (await Sharing.isAvailableAsync()) {
    await Sharing.shareAsync(printResult.uri, {
      dialogTitle: 'Lifecycle report',
      mimeType: 'application/pdf',
      UTI: 'com.adobe.pdf',
    });
    return;
  }

  throw new Error(`PDF generated (${filename}.pdf), but sharing is not available on this platform.`);
};
